import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { OsType } from './daemon';
import type { AgentConfig } from '@agentteams/runner-request/ui/lib/api';
export type { AgentConfig } from '@agentteams/runner-request/ui/lib/api';

type AgentConfigListResponse = {
  data: AgentConfig[];
  meta?: { page: number; pageSize: number; total: number; totalPages: number };
};

type AgentConfigResponse = {
  data: AgentConfig;
};

type AgentConfigListOptions = {
  includeDeleted?: boolean;
  mine?: boolean;
  page?: number;
  pageSize?: number;
};

/** `api/src/utils/pagination.ts`가 강제하는 상한. 이보다 크게 보내면 조용히 100으로 깎인다. */
const AGENT_CONFIG_MAX_PAGE_SIZE = 100;
/** 페이지 순회가 서버 응답 오류로 끝없이 도는 일이 없도록 두는 안전장치. */
const AGENT_CONFIG_MAX_PAGES = 50;

const fetchAgentConfigPage = async (
  token: string,
  projectId: string,
  options?: AgentConfigListOptions,
): Promise<AgentConfigListResponse> => {
  const params = new URLSearchParams();
  if (options?.includeDeleted) params.set('includeDeleted', 'true');
  if (options?.mine) params.set('mine', 'true');
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('pageSize', String(options.pageSize));
  const qs = params.toString();
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/agent-configs${qs ? `?${qs}` : ''}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch agent configs: ${response.status}`);
  }

  return (await response.json()) as AgentConfigListResponse;
};

/**
 * 프로젝트의 AgentConfig를 **전부** 가져온다. 페이지를 끝까지 넘기는 이유는
 * `findAgentConfigByName`과 같다 — 목록 API 기본 pageSize는 20인데 에이전트 수에는 상한이 없다.
 * 첫 페이지만 쓰면 21번째 이후 에이전트가 화면에서 통째로 사라지고, 그 에이전트만 가진 러너는
 * "에이전트 없는 러너"로 잘못 판정된다.
 *
 * 호출부가 page/pageSize를 명시하면 그 한 페이지만 돌려준다(기존 단일 페이지 호출 계약 유지).
 */
export const fetchAgentConfigs = async (
  token: string,
  projectId: string,
  options?: AgentConfigListOptions,
): Promise<AgentConfig[]> => {
  if (options?.page !== undefined || options?.pageSize !== undefined) {
    const { data } = await fetchAgentConfigPage(token, projectId, options);
    return data;
  }

  const readPage = (page: number) =>
    fetchAgentConfigPage(token, projectId, { ...options, page, pageSize: AGENT_CONFIG_MAX_PAGE_SIZE });

  const first = await readPage(1);
  const collected: AgentConfig[] = [...first.data];

  // 첫 응답이 전체 페이지 수를 알려 주므로 나머지는 병렬로 받는다. 직렬로 쌓으면
  // 에이전트가 100개를 넘는 프로젝트에서 왕복 지연이 페이지 수만큼 곱해진다.
  if (first.meta) {
    const totalPages = Math.min(first.meta.totalPages, AGENT_CONFIG_MAX_PAGES);
    if (totalPages > 1) {
      const rest = await Promise.all(Array.from({ length: totalPages - 1 }, (_, index) => readPage(index + 2)));
      rest.forEach(({ data }) => collected.push(...data));
    }
    return collected;
  }

  // meta가 없는 구 서버 응답은 전체 페이지 수를 모른다 — "가득 찬 페이지면 더 있다"로 추정하며 순차로 넘긴다.
  let lastPageSize = first.data.length;
  for (let page = 2; page <= AGENT_CONFIG_MAX_PAGES && lastPageSize === AGENT_CONFIG_MAX_PAGE_SIZE; page += 1) {
    const { data } = await readPage(page);
    collected.push(...data);
    lastPageSize = data.length;
  }

  return collected;
};

/**
 * 이름으로 AgentConfig 한 건을 찾는다. 페이지를 끝까지 넘긴다.
 *
 * 첫 페이지만 훑으면 안 되는 이유: 목록 API의 기본 pageSize는 20인데(`parsePagination`)
 * PRO 티어의 에이전트 수는 무제한이라, 워크트리를 많이 쓰는 프로젝트에서는 재사용 대상이
 * 21번째 이후로 밀려 영영 안 보인다. 그 상태에서는 같은 폴더의 `agentteams init`이 이름을
 * 바꾸기 전까지 항상 같은 지점에서 막힌다.
 */
export const findAgentConfigByName = async (
  token: string,
  projectId: string,
  name: string,
  options?: { includeDeleted?: boolean; mine?: boolean },
): Promise<AgentConfig | null> => {
  for (let page = 1; page <= AGENT_CONFIG_MAX_PAGES; page += 1) {
    const { data, meta } = await fetchAgentConfigPage(token, projectId, {
      ...options,
      page,
      pageSize: AGENT_CONFIG_MAX_PAGE_SIZE,
    });

    const match = data.find((config) => config.name === name);
    if (match) return match;

    // meta가 없는 구 서버 응답은 "가득 찬 페이지면 더 있다"로 추정한다.
    const hasMore = meta ? page < meta.totalPages : data.length === AGENT_CONFIG_MAX_PAGE_SIZE;
    if (!hasMore) return null;
  }

  return null;
};

export const fetchAgentConfig = async (token: string, projectId: string, id: string): Promise<AgentConfig> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/agent-configs/${id}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch agent config: ${response.status}`);
  }

  const data = (await response.json()) as AgentConfigResponse;
  return data.data;
};

export const createAgentConfig = async (
  token: string,
  projectId: string,
  input: {
    name: string;
    osType?: OsType;
    machineId?: string;
    /** 암호화된 작업 디렉터리. 서버는 복호화한 경로만 저장하고 응답에는 폴더명만 돌려준다. */
    authPathEnc?: string;
  },
): Promise<AgentConfig> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/agent-configs`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create agent config: ${response.status}`);
  }

  const data = (await response.json()) as AgentConfigResponse;
  return data.data;
};

export const updateAgentConfig = async (
  token: string,
  projectId: string,
  id: string,
  input: {
    name?: string;
    osType?: OsType | null;
    targetDaemonId?: string | null;
    machineId?: string;
    planModeOnly?: boolean;
    useWorktree?: boolean;
    shared?: boolean;
    /** 암호화된 작업 디렉터리. 생성자 본인 요청일 때만 반영된다(서버가 소유자를 판정한다). */
    authPathEnc?: string;
  },
): Promise<AgentConfig> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/agent-configs/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update agent config: ${response.status}`);
  }

  const data = (await response.json()) as AgentConfigResponse;
  return data.data;
};

export const deleteAgentConfig = async (token: string, projectId: string, id: string) => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/agent-configs/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete agent config: ${response.status}`);
  }
};

export type AgentApiKeyResponse = {
  apiKey: string;
};

export const issueAgentApiKey = async (
  token: string,
  projectId: string,
  agentConfigId: string,
  options?: { authPathEnc?: string },
): Promise<AgentApiKeyResponse> => {
  const body = options?.authPathEnc ? { authPathEnc: options.authPathEnc } : {};

  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/agent-configs/${agentConfigId}/api-keys`,
    {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to issue API key: ${response.status}`);
  }

  const data = (await response.json()) as { data: AgentApiKeyResponse };
  return data.data;
};

/**
 * 이 에이전트의 레거시 API 키(`key_`)를 폐기한다.
 *
 * 로컬에서 키 사본을 지우는 것(`agentteams mcp doctor`)과는 별개다. 사본을 다 지워도 서버의
 * 키는 그대로 인증에 통과하므로, 레거시 인증을 실제로 끊는 유일한 동작이 이 폐기다.
 * 발급(`issueAgentApiKey`)은 폐기 시각을 되돌려 다시 활성으로 만드는 점에 주의한다.
 */
export const revokeAgentApiKey = async (token: string, projectId: string, agentConfigId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/agent-configs/${agentConfigId}/api-keys`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to revoke API key: ${response.status}`);
  }
};
